import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { CrossIconBtn } from "assets/SVGs/SVGs";
import { useSelector } from "react-redux";
import { convertInRequiredTimeZone } from "utility";
import { titles } from "constants/titles";
import "./DeviceIdsModal.css";

const safeDeviceBlocks = (notification) => {
  return Array.isArray(notification?.device_ids) ? notification.device_ids : [];
};

const getCustomerDevices = (notification) => {
  return safeDeviceBlocks(notification).flatMap((block) =>
    Array.isArray(block?.customer_device_ids) ? block.customer_device_ids : []
  );
};

const getGuestDevices = (notification) => {
  return safeDeviceBlocks(notification).flatMap((block) =>
    Array.isArray(block?.guest_user_device_ids) ? block.guest_user_device_ids : []
  );
};

const getPlatformLabel = (deviceType) => {
  const t = (deviceType || "")?.toString()?.toUpperCase();
  if (t === "IOS") return "iOS";
  if (t === "ANDROID") return "Android";
  return deviceType || "";
};

const getPlatformClass = (deviceType) => {
  const t = (deviceType || "")?.toString()?.toUpperCase();
  if (t === "IOS") return "device_ids_badge_ios";
  if (t === "ANDROID") return "device_ids_badge_android";
  return "device_ids_badge_default";
};

const DeviceRow = ({ device }) => {
  const deviceId = device?.device_id;
  const deviceType = device?.device_type;
  const platformLabel = getPlatformLabel(deviceType);

  return (
    <div className="device_ids_row">
      <div className="device_ids_row_text">
        <div className="device_ids_row_title">
          <span className="device_ids_row_id_label">ID</span>
          <span className="device_ids_row_id_colon">:</span>{" "}
          <span className="device_ids_row_id_value">{deviceId ?? "-"}</span>
        </div>
      </div>
      {platformLabel ? (
        <div className={`device_ids_platform_badge ${getPlatformClass(deviceType)}`}>
          {platformLabel}
        </div>
      ) : null}
    </div>
  );
};

const DeviceIdsModal = ({ show, setShow, notification }) => {
  const [activeTab, setActiveTab] = useState("customer");
  const { currentTimeZone } = useSelector((store) => store?.common);

  const customerDevices = useMemo(
    () => getCustomerDevices(notification),
    [notification]
  );
  const guestDevices = useMemo(() => getGuestDevices(notification), [notification]);
  const totalDevices = (customerDevices?.length || 0) + (guestDevices?.length || 0);
  const timeText = useMemo(() => {
    const createdAt = notification?.created_at;
    if (!createdAt) return "";
    return convertInRequiredTimeZone(
      createdAt,
      notification?.medicalUserTimezone,
      currentTimeZone
    );
  }, [notification?.created_at, notification?.medicalUserTimezone, currentTimeZone]);

  const handleClose = () => {
    setShow(false);
  };

  return (
    <Modal
      backdrop="true"
      aria-labelledby="contained-modal-title-vcenter"
      className="device_ids_modal_container"
      show={!!show}
      onHide={handleClose}
      centered
    >
      <Modal.Body className="device_ids_modal_body">
        <div className="device_ids_modal_header">
          <div className="device_ids_modal_header_text">
            <h4 className="device_ids_modal_title">
              {titles?.notification_sent_to}
            </h4>
            <p className="device_ids_modal_subtitle">
              {totalDevices} {titles?.devices_label}
              {timeText ? ` \u00B7 ${timeText}` : ""}
            </p>
          </div>
          <div className="device_ids_modal_close">
            <CrossIconBtn onClick={handleClose} />
          </div>
        </div>

        <div className="device_ids_tabs">
          <button
            type="button"
            className={`device_ids_tab_btn ${
              activeTab === "customer" ? "device_ids_tab_active" : ""
            }`}
            onClick={() => setActiveTab("customer")}
          >
            Customer ({customerDevices?.length || 0})
          </button>
          <button
            type="button"
            className={`device_ids_tab_btn ${
              activeTab === "guest" ? "device_ids_tab_active" : ""
            }`}
            onClick={() => setActiveTab("guest")}
          >
            Guest ({guestDevices?.length || 0})
          </button>
        </div>

        <div className="device_ids_tab_content">
          {activeTab === "customer" ? (
            customerDevices?.length > 0 ? (
              <div className="device_ids_list_simple">
                {customerDevices?.map((d, idx) => (
                  <DeviceRow key={`${d?.device_id || "device"}-${idx}`} device={d} />
                ))}
              </div>
            ) : (
              <p className="device_ids_empty">No customer devices found.</p>
            )
          ) : guestDevices?.length > 0 ? (
            <div className="device_ids_list_simple">
              {guestDevices?.map((d, idx) => (
                <DeviceRow key={`${d?.device_id || "device"}-${idx}`} device={d} />
              ))}
            </div>
          ) : (
            <p className="device_ids_empty">No guest devices found.</p>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DeviceIdsModal;

